import { API_ACCEPTED_TIMEZONES } from './event-timezone-accepted';
import { EVENT_TIMEZONE_OFFSETS } from './event-timezone-offsets';

export type EventTimezone = (typeof EVENT_TIMEZONE_OFFSETS)[number]['value'];

export type EventTimezoneOption = {
  value: string;
  label: string;
  supportText: string;
  searchText: string;
};

export const EVENT_TIMEZONES: readonly string[] = EVENT_TIMEZONE_OFFSETS.map(
  (offset) => offset.value,
);

const byZone = new Map<string, { label: string }>(
  EVENT_TIMEZONE_OFFSETS.map((offset) => [offset.value, offset]),
);

const offsetMinutes = (timezone: string, at: Date): number | null => {
  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;

    const parsed = formatted?.match(/^GMT([+-])(\d{2}):(\d{2})$/);
    if (!parsed) return formatted === 'GMT' ? 0 : null;

    return (parsed[1] === '-' ? -1 : 1) * (Number(parsed[2]) * 60 + Number(parsed[3]));
  } catch {
    return null;
  }
};

/** Cities the API's zone list still names by their pre-rename id; only the label is corrected. */
const RENAMED_PLACES: Record<string, string> = {
  'Africa/Asmera': 'Asmara',
  'America/Godthab': 'Nuuk',
  'Asia/Calcutta': 'Kolkata',
  'Asia/Katmandu': 'Kathmandu',
  'Asia/Rangoon': 'Yangon',
  'Asia/Saigon': 'Ho Chi Minh City',
  'Atlantic/Faeroe': 'Faroe Islands',
  'Europe/Kiev': 'Kyiv',
  'Pacific/Ponape': 'Pohnpei',
  'Pacific/Truk': 'Chuuk',
};

const placeOf = (timezone: string): string =>
  RENAMED_PLACES[timezone] ??
  (timezone.split('/').slice(1).join(' – ').replace(/_/g, ' ') || timezone);

const formatOffset = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '+';
  const absolute = Math.abs(minutes);
  const rest = absolute % 60;

  return `GMT${sign}${Math.floor(absolute / 60)}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
};

/**
 * The offset a host reads on the row. Events saved before the picker became offset-based carry a
 * city zone, so anything not in the table is labelled from the offset it sits at today.
 */
export const timezoneLabel = (timezone: string): string => {
  const known = byZone.get(timezone);
  if (known) return known.label;

  const minutes = offsetMinutes(timezone, new Date());
  if (minutes === null) return timezone;

  return `${formatOffset(minutes)} · ${placeOf(timezone)}`;
};

export const EVENT_TIMEZONE_OPTIONS: EventTimezoneOption[] = EVENT_TIMEZONE_OFFSETS.map(
  (offset) => ({
    value: offset.value,
    label: offset.label,
    supportText: offset.hint,
    searchText: offset.cities,
  }),
);

/**
 * The picker only carries the offsets, so an event saved with a city zone would otherwise render as
 * an empty field on edit and lose its zone on the next save.
 */
export const eventTimezoneOptions = (current: string): EventTimezoneOption[] => {
  if (!current || byZone.has(current)) return EVENT_TIMEZONE_OPTIONS;

  return [
    ...EVENT_TIMEZONE_OPTIONS,
    { value: current, label: timezoneLabel(current), supportText: current, searchText: current },
  ];
};

/**
 * The viewer's own zone, kept whole rather than reduced to the offset it happens to sit at today.
 *
 * A fixed-offset row carries no DST rules, so seeding the form from the current offset would label
 * a New York host's December event GMT-4 — the offset their clock reads in September, not the one
 * the venue will be on. `eventTimezoneOptions` renders the zone as its own row, so the host still
 * sees a single unambiguous choice and can switch to a plain offset if they want one.
 *
 * Falls back to the matching offset row for a zone the API has not adopted, since submitting one is
 * a 400.
 */
export const eventTimezoneForLocalClock = (): string | null => {
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (API_ACCEPTED_TIMEZONES.has(local)) return local;

  const minutes = offsetMinutes(local, new Date());
  if (minutes === null) return null;

  return EVENT_TIMEZONE_OFFSETS.find((offset) => offset.minutes === minutes)?.value ?? null;
};
